import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useAuthStore } from '@/store/useAuthStore';
import { useNavigationStore } from '@/common/store';
import { useTutorialStore } from '@/store/useTutorialStore';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import Avatar from '@/common/components/avatar/Avatar';
import { 
  Check, 
  AlertTriangle,
  ArrowRight,
  Lock,
  Send,
  Rocket,
  TrendingUp,
  Lightbulb,
  Bell,
  ChevronRight,
  Inbox,
  Building2,
  Clock,
  Search
} from 'lucide-react';
import Config from '@/config';
import { authService } from '@/services/authService';
import { notificationService } from '@/services/notificationService';
import { Notification } from '@/common/types/notification';
import MySpaceTutorial from '@/components/tutorial/MySpaceTutorial';
import PersonalEmailVerificationModal from '@/components/PersonalEmailVerificationModal';
import { toastUtils } from '@/common/utils/toastUtils';
import { EntitlementSeverity } from '@/common/types/login';

interface CompanyUser {
  id: number;
  name: string;
  email: string;
  emailVerified: boolean;
  isMaster: boolean;
  company: {
    id: number;
    name: string;
    code: string;
    logo?: string;
    services: {
      type: SpaceType;
      isAvailable: boolean;
      isNotice: boolean;
      unavailableReason?: string;
      severity?: EntitlementSeverity;
    }[];
  };
}

type SpaceType = 'STARTUP' | 'INVESTOR' | 'INNOVATE';

/**
 * 마이스페이스 대시보드
 * - 공간 선택 + 알림 목록 통합
 */
const MySpacePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const { setHeaderTitle, setRestartIntro } = useNavigationStore();
  const { startOnboardingFlow, isTutorialCompleted, loadFromStorage, resetTutorial } = useTutorialStore();
  const [isSendingVerification, setIsSendingVerification] = useState(false);
  const [sendingCompanyId, setSendingCompanyId] = useState<number | null>(null);
  
  // 알림 상태
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const [totalNotifications, setTotalNotifications] = useState(0);
  
  // 튜토리얼 상태
  const [showTutorial, setShowTutorial] = useState(false);
  
  // 개인 이메일 인증 모달 상태
  const [isPersonalEmailModalOpen, setIsPersonalEmailModalOpen] = useState(false);

  useEffect(() => {
    
    setHeaderTitle('마이스페이스');
    
    // 인트로 재시작 함수 등록
    setRestartIntro(() => {
      resetTutorial('my-space');
      setTimeout(() => {
        setShowTutorial(true);
      }, 300);
    });
    
    if (!user) {
      navigate('/login');
      return;
    }

    fetchNotifications();
    
    // 튜토리얼 상태 로드
    loadFromStorage();
    
    // 튜토리얼 표시 여부 확인
    const tutorialParam = searchParams.get('tutorial');
    const isMySpaceTutorialCompleted = isTutorialCompleted('my-space');
    
    if (tutorialParam === 'true' && !isMySpaceTutorialCompleted) {
      // URL에서 tutorial 파라미터 제거
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      
      // 온보딩 플로우 시작
      startOnboardingFlow();
      
      // 약간의 딜레이 후 튜토리얼 시작 (페이지 렌더링 완료 후)
      setTimeout(() => {
        setShowTutorial(true);
      }, 500);
    }
    
    // cleanup: 페이지 떠날 때 인트로 재시작 함수 제거
    return () => {
      setRestartIntro(null);
    };
  }, [user, navigate, setHeaderTitle, setRestartIntro, resetTutorial, searchParams, loadFromStorage, isTutorialCompleted, startOnboardingFlow]);

  const fetchNotifications = async () => {
    try {
      setIsLoadingNotifications(true);
      const response = await notificationService.getNotifications(1, 5);
      setNotifications(response.data);
      setTotalNotifications(response.meta.totalItems);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setIsLoadingNotifications(false);
    }
  };

  const getSpaceUrl = (type: SpaceType, companyCode: string): string => {
    const urlMap: Record<SpaceType, string> = {
      STARTUP: `${Config.startupClientUrl}/${companyCode}/dashboard`,
      INVESTOR: `${Config.investorClientUrl}/${companyCode}/home`,
      INNOVATE: `${Config.innovateClientUrl}/${companyCode}/program/list`,
    };
    return urlMap[type];
  };

  const getSpaceName = (type: SpaceType): string => {
    const nameMap: Record<SpaceType, string> = {
      STARTUP: '스타트업',
      INVESTOR: '투자자',
      INNOVATE: '선발 프로그램',
    };
    return nameMap[type];
  };

  const getSpaceIcon = (type: SpaceType, size: number = 18) => {
    switch (type) {
      case 'STARTUP':
        return <Rocket size={size} />;
      case 'INVESTOR':
        return <TrendingUp size={size} />;
      case 'INNOVATE':
        return <Lightbulb size={size} />;
    }
  };

  const handleGoToSpace = (companyCode: string, space: SpaceType) => {
    localStorage.setItem('lastSelectedCompanyCode', companyCode);
    localStorage.setItem('lastSelectedSpace', space);
    window.location.href = getSpaceUrl(space, companyCode);
  };

  const handleResendVerification = async (companyUser: CompanyUser) => {
    setIsSendingVerification(true);
    setSendingCompanyId(companyUser.id);
    try {
      const result = await authService.resendVerifyEmail(companyUser.email, companyUser.id);
      if (result.success) {
        toastUtils.success('인증 메일이 발송되었습니다. 이메일을 확인해주세요.');
      } else {
        toastUtils.error(result.message || '인증 메일 발송에 실패했습니다.');
      }
    } catch (error) {
      toastUtils.error('인증 메일 발송 중 오류가 발생했습니다.');
    } finally {
      setIsSendingVerification(false);
      setSendingCompanyId(null);
    }
  };

  const formatNotificationTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (minutes < 1) return '방금 전';
    if (minutes < 60) return `${minutes}분 전`;
    if (hours < 24) return `${hours}시간 전`;
    if (days < 7) return `${days}일 전`;
    
    return date.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
  };

  const handleFindCompanyClick = () => {
    if (!user?.emailVerified) {
      setIsPersonalEmailModalOpen(true);
      return;
    }
    navigate('/find-company');
  };

  const companyUsers: CompanyUser[] = ((user?.companyUsers ?? []) as CompanyUser[]).filter(
    (companyUser) => Boolean(companyUser?.company),
  );

  if (!user) {
    return null;
  }

  return (
    <PageContainer>
      {/* 개인 이메일 인증 모달 */}
      <PersonalEmailVerificationModal
        isOpen={isPersonalEmailModalOpen}
        onClose={() => setIsPersonalEmailModalOpen(false)}
        userEmail={user?.email}
      />
      
      {/* 튜토리얼 */}
      <MySpaceTutorial 
        run={showTutorial} 
        onComplete={() => setShowTutorial(false)} 
      />
      
      <ContentWrapper>
        {/* 히어로 섹션 */}
        <HeroSection data-tutorial="hero-section">
          <HeroBackground>
            <HeroGradient />
            <HeroPattern />
          </HeroBackground>
          <HeroContent>
            <HeroAvatarWrapper>
              <Avatar src={user.profileImage} alt={user.name} isUser={true} sizes="xl" />              
            </HeroAvatarWrapper>
            <HeroTextContent>
              <HeroGreeting>안녕하세요,</HeroGreeting>
              <HeroName>{user.name}님! 👋</HeroName>
              <HeroDescription>
                오늘도 좋은 하루 되세요. 아래에서 공간을 선택하거나 알림을 확인하세요.
              </HeroDescription>
            </HeroTextContent>
            <HeroStats>
              <HeroStatItem>
                <HeroStatIcon $type="company">
                  <Building2 size={18} />
                </HeroStatIcon>
                <HeroStatInfo>
                  <HeroStatValue>{companyUsers.length}</HeroStatValue>
                  <HeroStatLabel>소속 회사</HeroStatLabel>
                </HeroStatInfo>
              </HeroStatItem>
              <HeroStatDivider />
              <HeroStatItem>
                <HeroStatIcon $type="notification">
                  <Bell size={18} />
                </HeroStatIcon>
                <HeroStatInfo>
                  <HeroStatValue>{totalNotifications}</HeroStatValue>
                  <HeroStatLabel>알림</HeroStatLabel>
                </HeroStatInfo>
              </HeroStatItem>
            </HeroStats>
          </HeroContent>
        </HeroSection>

        <DashboardGrid>
          {/* 내 공간 카드 */}
          <DashboardCard data-tutorial="company-section">
            <CardHeader>
              <CardTitleWrapper>
                <CardIconWrapper $variant="primary">
                  <Building2 size={20} />
                </CardIconWrapper>
                <div>
                  <CardTitle>내 공간</CardTitle>
                  <CardSubtitle>소속된 회사 및 스페이스</CardSubtitle>
                </div>
              </CardTitleWrapper>
              {companyUsers.length > 0 && (
                <CardBadge>{companyUsers.length}개</CardBadge>
              )}
            </CardHeader>

            <CardContent>
              {/* 회사 찾기 배너 - 항상 표시 */}
              <FindCompanyBanner onClick={handleFindCompanyClick}>
                <FindCompanyBannerContent>
                  <FindCompanyIconWrapper>
                    <Search size={24} />
                  </FindCompanyIconWrapper>
                  <FindCompanyTextWrapper>
                    <FindCompanyTitle>회사에 참여하기</FindCompanyTitle>
                    <FindCompanyDesc>
                      나의 소속 회사를 검색하여 가입 신청하세요.<br />
                      회사 마스터 승인 후 직원으로 등록됩니다.
                    </FindCompanyDesc>
               
                  </FindCompanyTextWrapper>
                </FindCompanyBannerContent>
                <FindCompanyArrow>
                  <ArrowRight size={20} />
                </FindCompanyArrow>
              </FindCompanyBanner>

              {companyUsers.length === 0 ? (
                <EmptyState>
                  <EmptyIconWrapper>
                    <Inbox size={48} />
                  </EmptyIconWrapper>
                  <EmptyTitle>아직 소속된 회사가 없습니다</EmptyTitle>
                  <EmptyDescription>
                    위 버튼을 눌러 회사에 참여하거나,<br />
                    초대 링크를 통해 회사에 가입해보세요.
                  </EmptyDescription>
                </EmptyState>
              ) : (
                <CompanyList>
                  {companyUsers.map((companyUser, index) => (
                    <CompanyCard key={companyUser.id} $delay={index * 0.1}>
                      <CompanyCardHeader>
                        <CompanyAvatarWrapper>
                          <Avatar 
                            alt={companyUser.company.name} 
                            src={companyUser.company.logo}
                            isUser={false} 
                            sizes="md"
                          />
                        </CompanyAvatarWrapper>
                        <CompanyInfo>
                          <CompanyNameRow>
                            <CompanyName>{companyUser.company.name}</CompanyName>
                            {companyUser.emailVerified ? (
                              <VerifiedBadge>
                                <Check size={12} />
                                인증완료
                              </VerifiedBadge>
                            ) : (
                              <UnverifiedBadge>
                                <Lock size={12} />
                                인증필요
                              </UnverifiedBadge>
                            )}
                          </CompanyNameRow>
                          <CompanyMeta>
                            <CompanyRole $isMaster={companyUser.isMaster}>
                              {companyUser.isMaster ? '👑 마스터' : '멤버'}
                            </CompanyRole>
                            <CompanySpaceCount>
                              {companyUser.company.services?.length || 0}개 스페이스
                            </CompanySpaceCount>
                          </CompanyMeta>
                        </CompanyInfo>
                      </CompanyCardHeader>

                      {companyUser.emailVerified ? (
                        <SpaceButtonGroup>
                          {companyUser.company.services?.map((service) => (
                            <SpaceButton
                              key={service.type}
                              $type={service.type}
                              onClick={() => handleGoToSpace(companyUser.company.code, service.type)}
                            >
                              <SpaceButtonIcon $type={service.type}>
                                {getSpaceIcon(service.type, 16)}
                              </SpaceButtonIcon>
                              <span>{getSpaceName(service.type)}</span>
                              {service.type === 'INVESTOR' && service.severity && service.severity !== 'NONE' && (
                                service.severity === 'INFO' ? (
                                  <EntitlementDot title="이용권 안내가 있습니다" />
                                ) : (
                                  <EntitlementTag $severity={service.severity}>
                                    {service.severity === 'CRITICAL' ? '이용 제한' : '한도 초과'}
                                  </EntitlementTag>
                                )
                              )}
                              <SpaceButtonArrow>
                                <ChevronRight size={14} />
                              </SpaceButtonArrow>
                            </SpaceButton>
                          ))}
                        </SpaceButtonGroup>
                      ) : (
                        <VerificationWarning>
                          <WarningIconWrapper>
                            <AlertTriangle size={20} />
                          </WarningIconWrapper>
                          <WarningContent>
                            <WarningTitle>이메일 인증이 필요합니다</WarningTitle>
                            <WarningDescription>
                              {companyUser.email}로 인증 메일을 발송해주세요.
                            </WarningDescription>
                          </WarningContent>
                          <VerifyButton
                            onClick={() => handleResendVerification(companyUser)}
                            disabled={isSendingVerification && sendingCompanyId === companyUser.id}
                          >
                            {isSendingVerification && sendingCompanyId === companyUser.id ? (
                              <LoadingDots>발송중</LoadingDots>
                            ) : (
                              <>
                                <Send size={14} />
                                인증메일 발송
                              </>
                            )}
                          </VerifyButton>
                        </VerificationWarning>
                      )}
                    </CompanyCard>
                  ))}
                </CompanyList>
              )}
            </CardContent>
          </DashboardCard>

          {/* 최근 알림 카드 */}
          <DashboardCard data-tutorial="notification-section">
            <CardHeader>
              <CardTitleWrapper>
                <CardIconWrapper $variant="warning">
                  <Bell size={20} />
                </CardIconWrapper>
                <div>
                  <CardTitle>최근 알림</CardTitle>
                  <CardSubtitle>새로운 소식과 할 일</CardSubtitle>
                </div>
              </CardTitleWrapper>
              {totalNotifications > 0 && (
                <ViewAllButton onClick={() => navigate('/notifications')}>
                  전체 보기
                  <ChevronRight size={16} />
                </ViewAllButton>
              )}
            </CardHeader>

            <CardContent>
              {isLoadingNotifications ? (
                <LoadingContainer>
                  <LoadingSpinner />
                  <LoadingText>알림을 불러오는 중...</LoadingText>
                </LoadingContainer>
              ) : notifications.length === 0 ? (
                <EmptyState>
                  <EmptyIconWrapper>
                    <Bell size={48} />
                  </EmptyIconWrapper>
                  <EmptyTitle>알림이 없습니다</EmptyTitle>
                  <EmptyDescription>
                    새로운 알림이 오면 여기에 표시됩니다.
                  </EmptyDescription>
                </EmptyState>
              ) : (
                <NotificationList>
                  {notifications.map((notification, index) => (
                    <NotificationItem 
                      key={notification.id}
                      $delay={index * 0.05}
                      onClick={() => {
                        if (notification.url) {                          
                          window.open(notification.url + `?n_id=${notification.id}`, '_blank', 'noopener,noreferrer');
                        }
                      }}
                      $hasLink={!!notification.url}
                    >
                      <NotificationIconWrapper $type={notification.type}>
                        {notification.type === 'TASK' ? (
                          <Check size={16} />
                        ) : (
                          <Bell size={16} />
                        )}
                      </NotificationIconWrapper>
                      <NotificationContent>
                        <NotificationTitle>{notification.message}</NotificationTitle>
                        <NotificationMeta>
                          <NotificationTime>
                            <Clock size={12} />
                            {formatNotificationTime(notification.createdAt)}
                          </NotificationTime>
                          {notification.company && (
                            <NotificationCompanyBadge>
                              {notification.company.name}
                            </NotificationCompanyBadge>
                          )}
                        </NotificationMeta>
                      </NotificationContent>
                      {notification.url && (
                        <NotificationArrow>
                          <ChevronRight size={18} />
                        </NotificationArrow>
                      )}
                    </NotificationItem>
                  ))}
                </NotificationList>
              )}
            </CardContent>

            {totalNotifications > 5 && (
              <CardFooter>
                <FooterButton onClick={() => navigate('/notifications')}>
                  <span>{totalNotifications - 5}개의 알림 더 보기</span>
                  <ArrowRight size={16} />
                </FooterButton>
              </CardFooter>
            )}
          </DashboardCard>
        </DashboardGrid>
      </ContentWrapper>
    </PageContainer>
  );
};

// ============================================
// Animations
// ============================================

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

// ============================================
// Styled Components
// ============================================

const PageContainer = styled.div`
  min-height: 100%;
  height: 100%;
  padding: 1.5rem;
  background: ${semanticColors.background.primary};
  overflow-y: auto;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`;

// 히어로 섹션
const HeroSection = styled.div`
  position: relative;
  margin-bottom: 2rem;
  border-radius: 24px;
  overflow: hidden;
  animation: ${fadeInUp} 0.6s ease-out;
`;

const HeroBackground = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #6366f1 100%);
`;

const HeroGradient = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 30% 20%, rgba(255,255,255,0.15) 0%, transparent 50%),
              radial-gradient(circle at 70% 80%, rgba(255,255,255,0.1) 0%, transparent 40%);
`;

const HeroPattern = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.1;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
`;

const HeroContent = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 2rem;
  padding: 2.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const HeroAvatarWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const HeroAvatarBadge = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  border: 3px solid #1e3a8a;
  border-radius: 50%;
  color: #fff;
`;

const HeroTextContent = styled.div`
  flex: 1;
`;

const HeroGreeting = styled.p`
  ${typography.paragraph.p2.default}
  color: rgba(255,255,255,0.95);
  margin-bottom: 0.25rem;
`;

const HeroName = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #fff;
  margin-bottom: 0.5rem;
`;

const HeroDescription = styled.p`
  ${typography.paragraph.p2.default}
  color: rgba(255,255,255,0.9);
  max-width: 400px;
`;

const HeroStats = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1rem 1.5rem;
  background: rgba(255,255,255,0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 16px;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const HeroStatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const HeroStatIcon = styled.div<{ $type: 'company' | 'notification' }>`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  color: #fff;
  
  ${props => props.$type === 'company' && `
    background: rgba(59, 130, 246, 0.3);
  `}
  
  ${props => props.$type === 'notification' && `
    background: rgba(251, 191, 36, 0.3);
  `}
`;

const HeroStatInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeroStatValue = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
`;

const HeroStatLabel = styled.span`
  ${typography.caption.caption2}
  color: rgba(255,255,255,0.9);
`;

const HeroStatDivider = styled.div`
  width: 1px;
  height: 40px;
  background: rgba(255,255,255,0.2);
`;

// 대시보드 그리드
const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

// 대시보드 카드
const DashboardCard = styled.div`
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  min-height: 400px;
  animation: ${fadeInUp} 0.6s ease-out 0.2s both;
  
  &:nth-child(2) {
    animation-delay: 0.3s;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid ${semanticColors.border.primary};
`;

const CardTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const CardIconWrapper = styled.div<{ $variant: 'primary' | 'warning' }>`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  
  ${props => props.$variant === 'primary' && `
    background: linear-gradient(135deg, ${semanticColors.background.tertiary} 0%, ${semanticColors.background.tertiaryHover} 100%);
    color: ${semanticColors.text.brand};
  `}
  
  ${props => props.$variant === 'warning' && `
    background: linear-gradient(135deg, ${semanticColors.background.warning.secondary} 0%, ${semanticColors.background.warning.secondaryHover} 100%);
    color: ${semanticColors.text.onWarning.primary};
  `}
`;

const CardTitle = styled.h2`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
`;

const CardSubtitle = styled.p`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
  margin-top: 0.125rem;
`;

const CardBadge = styled.span`
  padding: 0.375rem 0.875rem;
  background: ${semanticColors.background.tertiary};
  border-radius: 100px;
  ${typography.caption.caption1.medium}
  color: ${semanticColors.text.brand};
`;

const CardContent = styled.div`
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
`;

const CardFooter = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid ${semanticColors.border.primary};
`;

const FooterButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.875rem;
  background: ${semanticColors.background.primary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 10px;
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${semanticColors.background.primaryHover};
    color: ${semanticColors.text.brand};
    border-color: ${semanticColors.border.brand};
  }
`;

const ViewAllButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 0.875rem;
  background: ${semanticColors.background.tertiary};
  border: none;
  border-radius: 8px;
  ${typography.caption.caption1.medium}
  color: ${semanticColors.text.brand};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${semanticColors.background.tertiaryHover};
  }
`;

// 회사 리스트
const CompanyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CompanyCard = styled.div<{ $delay: number }>`
  padding: 1.25rem;
  background: ${semanticColors.background.primary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 16px;
  transition: all 0.2s ease;
  animation: ${fadeIn} 0.4s ease-out ${props => props.$delay}s both;
  
  &:hover {
    border-color: ${semanticColors.border.secondary};
    box-shadow: 0 4px 12px rgba(0,0,0,0.04);
  }
`;

const CompanyCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const CompanyAvatarWrapper = styled.div`
  flex-shrink: 0;
`;

const CompanyInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CompanyNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
`;

const CompanyName = styled.h3`
  ${typography.subtitle.subtitle2}
  color: ${semanticColors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CompanyMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const CompanyRole = styled.span<{ $isMaster: boolean }>`
  ${typography.caption.caption2}
  color: ${props => props.$isMaster ? semanticColors.text.brand : semanticColors.text.tertiary};
`;

const CompanySpaceCount = styled.span`
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
  
  &::before {
    content: '•';
    margin-right: 0.75rem;
    color: ${semanticColors.border.primary};
  }
`;

const VerifiedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  background: ${semanticColors.background.success.secondary};
  border-radius: 6px;
  ${typography.caption.caption2}
  color: ${semanticColors.text.onSuccess.primary};
`;

const UnverifiedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  background: ${semanticColors.background.warning.secondary};
  border-radius: 6px;
  ${typography.caption.caption2}
  color: ${semanticColors.text.onWarning.primary};
`;

const SpaceButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const SpaceButton = styled.button<{ $type: SpaceType }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: ${semanticColors.background.secondary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 10px;
  ${typography.caption.caption1.medium}
  color: ${semanticColors.text.primary};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    ${props => props.$type === 'STARTUP' && `
      background: ${semanticColors.background.tertiary};
      border-color: ${semanticColors.border.brand};
      color: ${semanticColors.text.brand};
    `}
    
    ${props => props.$type === 'INVESTOR' && `
      background: ${semanticColors.background.success.secondary};
      border-color: ${semanticColors.border.success};
      color: ${semanticColors.text.onSuccess.primary};
    `}
    
    ${props => props.$type === 'INNOVATE' && `
      background: ${semanticColors.background.warning.secondary};
      border-color: ${semanticColors.border.warning};
      color: ${semanticColors.text.onWarning.primary};
    `}
  }
`;

const SpaceButtonIcon = styled.span<{ $type: SpaceType }>`
  display: flex;
  align-items: center;
  justify-content: center;
  
  ${props => props.$type === 'STARTUP' && `color: ${semanticColors.text.brand};`}
  ${props => props.$type === 'INVESTOR' && `color: ${semanticColors.text.onSuccess.primary};`}
  ${props => props.$type === 'INNOVATE' && `color: ${semanticColors.text.onWarning.primary};`}
`;

const SpaceButtonArrow = styled.span`
  display: flex;
  align-items: center;
  color: ${semanticColors.text.tertiary};
  margin-left: auto;
`;

const EntitlementDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #3b82f6;
  flex-shrink: 0;
`;

const EntitlementTag = styled.span<{ $severity: 'WARNING' | 'CRITICAL' }>`
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  border-radius: 100px;
  ${typography.caption.caption2}
  font-weight: 700;
  flex-shrink: 0;
  background: ${p => p.$severity === 'CRITICAL' ? '#fee2e2' : '#ffedd5'};
  color: ${p => p.$severity === 'CRITICAL' ? '#dc2626' : '#c2410c'};
`;

const VerificationWarning = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  background: ${semanticColors.background.warning.secondary};
  border: 1px solid ${semanticColors.border.warning};
  border-radius: 12px;
`;

const WarningIconWrapper = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.warning.primary};
  border-radius: 10px;
  color: ${semanticColors.text.white};
  flex-shrink: 0;
`;

const WarningContent = styled.div`
  flex: 1;
`;

const WarningTitle = styled.p`
  ${typography.subtitle.subtitle2}
  color: ${semanticColors.text.onWarning.primary};
  margin-bottom: 0.25rem;
`;

const WarningDescription = styled.p`
  ${typography.caption.caption2}
  color: ${semanticColors.text.onWarning.primary};
  opacity: 0.8;
`;

const VerifyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  background: ${semanticColors.background.warning.primary};
  border: none;
  border-radius: 8px;
  ${typography.caption.caption1.medium}
  color: ${semanticColors.text.white};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
  
  &:hover:not(:disabled) {
    background: ${semanticColors.background.warning.primaryHover};
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const LoadingDots = styled.span`
  &::after {
    content: '...';
    animation: ${pulse} 1s ease-in-out infinite;
  }
`;

// 알림 리스트
const NotificationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const NotificationItem = styled.div<{ $hasLink: boolean; $delay: number }>`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  background: ${semanticColors.background.primary};
  border: 1px solid ${semanticColors.border.primary};
  border-radius: 12px;
  cursor: ${props => props.$hasLink ? 'pointer' : 'default'};
  transition: all 0.2s ease;
  animation: ${fadeIn} 0.4s ease-out ${props => props.$delay}s both;
  
  ${props => props.$hasLink && `
    &:hover {
      background: ${semanticColors.background.primaryHover};
      border-color: ${semanticColors.border.secondary};
      transform: translateX(4px);
    }
  `}
`;

const NotificationIconWrapper = styled.div<{ $type: string }>`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  flex-shrink: 0;
  
  ${props => props.$type === 'TASK' && `
    background: ${semanticColors.background.tertiary};
    color: ${semanticColors.text.brand};
  `}
  
  ${props => props.$type !== 'TASK' && `
    background: ${semanticColors.background.warning.secondary};
    color: ${semanticColors.text.onWarning.primary};
  `}
`;

const NotificationContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const NotificationTitle = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.primary};
  margin-bottom: 0.375rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const NotificationMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const NotificationTime = styled.span`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  ${typography.caption.caption2}
  color: ${semanticColors.text.tertiary};
`;

const NotificationCompanyBadge = styled.span`
  padding: 0.125rem 0.5rem;
  background: ${semanticColors.background.tertiary};
  border-radius: 4px;
  ${typography.caption.caption2}
  color: ${semanticColors.text.brand};
`;

const NotificationArrow = styled.div`
  color: ${semanticColors.text.tertiary};
  flex-shrink: 0;
  transition: transform 0.2s ease;
  
  ${NotificationItem}:hover & {
    transform: translateX(4px);
    color: ${semanticColors.text.brand};
  }
`;

// 빈 상태
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 250px;
  text-align: center;
  padding: 2rem;
`;

const EmptyIconWrapper = styled.div`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${semanticColors.background.primary};
  border: 2px dashed ${semanticColors.border.primary};
  border-radius: 20px;
  color: ${semanticColors.text.disabled};
  margin-bottom: 1.5rem;
`;

const EmptyTitle = styled.h3`
  ${typography.subtitle.subtitle1}
  color: ${semanticColors.text.primary};
  margin-bottom: 0.5rem;
`;

const EmptyDescription = styled.p`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.tertiary};
  margin-bottom: 1.5rem;
`;

// 회사 찾기 배너
const FindCompanyBanner = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1.25rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: left;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 80% 20%, rgba(255,255,255,0.2) 0%, transparent 50%);
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 28px rgba(102, 126, 234, 0.4);
  }
  
  &:hover > div:last-child {
    transform: translateX(4px);
  }
`;

const FindCompanyBannerContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;
  z-index: 1;
`;

const FindCompanyIconWrapper = styled.div`
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 14px;
  color: white;
  flex-shrink: 0;
`;

const FindCompanyTextWrapper = styled.div`
  flex: 1;
`;

const FindCompanyTitle = styled.p`
  ${typography.subtitle.subtitle1}
  color: white;
  margin-bottom: 0.25rem;
`;

const FindCompanyDesc = styled.p`
  ${typography.caption.caption1.default}
  color: rgba(255, 255, 255, 0.85);
`;

const FindCompanyArrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  color: white;
  flex-shrink: 0;
  transition: transform 0.3s ease;
  position: relative;
  z-index: 1;
`;

// 로딩
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 250px;
  gap: 1rem;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${semanticColors.border.primary};
  border-top-color: ${semanticColors.background.brand};
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const LoadingText = styled.p`
  ${typography.caption.caption1.default}
  color: ${semanticColors.text.tertiary};
`;

export default MySpacePage;
